import type {
    MenuButtonCollapseOverridesConstantType,
    MenuButtonCollapseUpdateConstantType,
    MenuButtonCollapseApplyConstantType,
    MenuButtonCollapseKeyConstantType,
    MenuButtonNavigateConstantType,
    MenuButtonEndIconConstantType,
    MenuButtonTitleConstantType,
    MenuButtonCollapseState,
    MenuButton
} from "./type";
import {
    NCoreUIKitLocalize
} from "../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../types";
import {
    ChevronRight as ChevronRightIcon
} from "lucide-react-native";
import {
    getMirrorStyle,
    isMirroredIcon
} from "../../helpers/direction";

export const MenuRedirectIcon: NCoreUIKitIcon = ({
    customColor,
    size
}) => {
    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    return <ChevronRightIcon
        style={getMirrorStyle({
            isMirrored: isMirroredIcon({
                name: "ChevronRight"
            }),
            direction
        })}
        color={customColor}
        size={size}
    />;
};

export const getMenuButtonCollapseKey = ({
    depthMap
}: MenuButtonCollapseKeyConstantType): string => {
    return depthMap.join("-");
};

export const getMenuButtonsWithCollapse = <T extends MenuButton>({
    collapseOverrides,
    depthMap = [],
    buttons
}: MenuButtonCollapseOverridesConstantType<T>): Array<T> => {
    if(!Object.keys(collapseOverrides).length) {
        return buttons;
    }

    return buttons.map((buttonItem, buttonIndex) => {
        const currentDepthMap = [
            ...depthMap,
            buttonIndex
        ];

        const isCollapse = collapseOverrides[getMenuButtonCollapseKey({
            depthMap: currentDepthMap
        })];

        return {
            ...buttonItem,
            subButtons: buttonItem.subButtons ? getMenuButtonsWithCollapse({
                buttons: buttonItem.subButtons,
                depthMap: currentDepthMap,
                collapseOverrides
            }) : buttonItem.subButtons,
            isCollapse: isCollapse ?? buttonItem.isCollapse
        };
    });
};

export const getUpdatedMenuButtons = <T extends MenuButton>({
    depthIndex = 0,
    isCollapse,
    depthMap,
    buttons
}: MenuButtonCollapseUpdateConstantType<T>): Array<T> => {
    const targetIndex = depthMap[depthIndex];

    return buttons.map((buttonItem, buttonIndex) => {
        if(buttonIndex !== targetIndex) {
            return buttonItem;
        }

        if(depthIndex === depthMap.length - 1) {
            return {
                ...buttonItem,
                isCollapse
            };
        }

        return {
            ...buttonItem,
            subButtons: getUpdatedMenuButtons({
                buttons: buttonItem.subButtons ?? [],
                depthIndex: depthIndex + 1,
                isCollapse,
                depthMap
            })
        };
    });
};

export const updateMenuButtonCollapse = <T extends MenuButtonCollapseState>({
    setCollapseOverrides,
    depthMap,
    status,
    store,
    id
}: MenuButtonCollapseApplyConstantType<T>): void => {
    const state = store.get({
        id
    });

    if(!state) {
        setCollapseOverrides((previousOverrides) => ({
            ...previousOverrides,
            [getMenuButtonCollapseKey({
                depthMap
            })]: status
        }));

        return;
    }

    state.buttons = getUpdatedMenuButtons({
        buttons: state.buttons,
        isCollapse: status,
        depthMap
    });

    store.update({
        menuData: state,
        id: id
    });
};

export const navigateMenuButton = ({
    closeAction,
    buttonItem,
    navigation
}: MenuButtonNavigateConstantType): void => {
    if(buttonItem.redirectMain && buttonItem.redirectSub) {
        navigation.navigate(buttonItem.redirectMain, {
            screen: buttonItem.redirectSub
        });
    } else if(buttonItem.redirectMain) {
        navigation.navigate(buttonItem.redirectMain);
    }

    if(buttonItem.props?.onPress) {
        buttonItem.props.onPress();
    }

    if(closeAction) {
        closeAction();
    }
};

export const getMenuButtonTitle = ({
    buttonItem,
    localize
}: MenuButtonTitleConstantType): string => {
    return buttonItem.isUseLocalize ? localize(buttonItem.title as keyof NCoreUIKit.Translation) : buttonItem.title;
};

export const getMenuButtonEndIcon = ({
    collapsibleIcon,
    buttonItem
}: MenuButtonEndIconConstantType): NCoreUIKitIcon | undefined => {
    if(buttonItem.isCollapsible) {
        return collapsibleIcon;
    }

    if(typeof buttonItem.isShowRedirectIcon === "function") {
        return buttonItem.isShowRedirectIcon;
    }

    if(buttonItem.isShowRedirectIcon) {
        return MenuRedirectIcon;
    }

    return buttonItem.props?.rightIcon;
};
